import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  OnboardingSpotlight,
  OnboardingStepKeyEnum,
  useOnboardingRestart,
  useOnboardingStep,
} from "../../../components/Onboarding";
import { useJwt } from "../../../helpers/common";
import { setOrganizationOnboardSettings } from "../../../redux/slices/organization";
import { useOrganizationService } from "../../../services";

export interface GuiderStepConfig {
  key: string;
  divId?: string; // Static ID
  getDiv?: () => HTMLElement | null; // Dynamic finder
  waitFor?: () => Promise<HTMLElement | null>; // Async finder
  message?: string | ((context: any) => string);
  onNext?: (context: any) => void | Promise<void>;
  placement?: "top" | "bottom" | "left" | "right";
  condition?: (context: any) => boolean;
  /**
   * If provided, this function will be used to render a custom modal instead of the spotlight.
   */
  renderModal?: (props: {
    isOpen: boolean;
    onClose: () => void;
    context: any;
  }) => React.ReactNode;
}

interface GuiderProps {
  onboardingKey: OnboardingStepKeyEnum;
  steps: GuiderStepConfig[];
  initialContext?: any;
  /**
   * The key in onboard_settings to update upon completion.
   * If not provided, the onboardingKey will be used.
   */
  onCompleteSettingsKey?: string;
  onComplete?: () => void;
}

export function Guider({
  onboardingKey,
  steps,
  initialContext = {},
  onCompleteSettingsKey,
  onComplete,
}: GuiderProps) {
  const dispatch = useDispatch();
  const location = useLocation();
  const JWTdata = useJwt() as any;
  const { updateOrganization } = useOrganizationService();
  const { onboardSettings = {} } = useSelector((s: any) => s.organization);

  const restartToken = useOnboardingRestart(onboardingKey);

  const { show: parentShow, complete: completeRemote } = useOnboardingStep(
    onboardingKey,
    { shouldShow: true },
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [context, setContext] = useState<any>(initialContext);

  // Handle restart
  useEffect(() => {
    if (restartToken) {
      setPaused(false);
      setCurrentStepIndex(0);
      setContext(initialContext);
    }
  }, [restartToken]);

  // Find target for current step
  useEffect(() => {
    let cancelled = false;
    const findTarget = async () => {
      if (!parentShow || paused) return;

      const step = steps[currentStepIndex];
      if (!step) return;

      // Skip if condition met (handled by separate effect, but being safe here)
      if (step.condition && !step.condition(context)) {
        return;
      }

      // If it's a modal step, we don't need a target element for spotlight
      if (step.renderModal) {
        if (!cancelled) setTargetEl(null);
        return;
      }

      let el: HTMLElement | null = null;
      if (step.divId) {
        el = document.querySelector(step.divId) as HTMLElement;

      } else if (step.getDiv) {
        el = step.getDiv();
      } else if (step.waitFor) {
        el = await step.waitFor();
      }
      
      if (!el && !step.renderModal) {
        handleNext(false);
        return;
      }
      

      // Special handling to save context (like risk entity) if needed by consumers
      // We'll keep it generic: check for custom attributes if needed, or consumers can use onNext
      if (el && el.hasAttribute("data-onboarding-entity-id")) {
        const id = el.getAttribute("data-onboarding-entity-id");
        const name = el.getAttribute("data-onboarding-entity-name");
        if (id)
          setContext((prev: any) => ({ ...prev, entityId: id, entityName: name }));
      }

      if (!cancelled) setTargetEl(el);
    };

    setTargetEl(null);
    findTarget();
    return () => {
      cancelled = true;
    };
  }, [currentStepIndex, parentShow, paused, steps, context, location.pathname]);

  const handleNext = async (runClientSideFun = true) => {
    const step = steps[currentStepIndex];
    if (step && step.onNext) {
      if(runClientSideFun) await step.onNext(context);
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    setPaused(true);
    const settingsKey = onCompleteSettingsKey || onboardingKey;
    const next = {
      ...onboardSettings,
      [settingsKey]: true,
    };
    dispatch(setOrganizationOnboardSettings(next));
    try {
      await updateOrganization(
        JWTdata?.org_id,
        { onboard_settings: next },
        true,
      );
      completeRemote();
      onComplete?.();
    } catch (e) {
      console.error("Failed to update onboarding settings", e);
    }

    // Generic event for closing tooltips if needed
    if (context.entityId) {
      window.dispatchEvent(
        new CustomEvent("cadence:risk-close", {
          detail: { id: context.entityId },
        }),
      );
    }
  };

  // Skip logic
  useEffect(() => {
    if (!parentShow || paused) return;
    const step = steps[currentStepIndex];
    if (!step) return;

    const conditionFailed = step.condition && !step.condition(context);
    const noVisual =
      !step.renderModal && !step.divId && !step.getDiv && !step.waitFor;

    if (conditionFailed || noVisual) {
      handleNext();
    }
  }, [currentStepIndex, steps, context, parentShow, paused]);

  const handleSkip = () => {
    handleComplete();
  };

  if (!parentShow || paused || !steps[currentStepIndex]) return null;

  const currentStep = steps[currentStepIndex];

  // If the step is a modal step
  if (currentStep.renderModal) {
    return (
      <>
        {currentStep.renderModal({
          isOpen: true,
          onClose: handleNext,
          context,
        })}
      </>
    );
  }

  // If no target found for spotlight, we can't show it (unless it's a modal)
  if (!targetEl) return null;

  const message =
    typeof currentStep.message === "function"
      ? currentStep.message(context)
      : currentStep.message || "";

  return (
    <OnboardingSpotlight
      key={`${currentStep.key}-${restartToken}`}
      targetRef={{ current: targetEl }}
      message={message}
      onDismiss={handleNext}
      onSkip={handleSkip}
      variant="spotlight"
      stepIndex={currentStepIndex}
      totalSteps={steps.length}
      primaryTitle={currentStepIndex === steps.length - 1 ? "Finish" : "Next"}
      preferredPlacements={
        currentStep.placement
          ? [currentStep.placement]
          : ["bottom", "top", "left", "right"]
      }
    />
  );
}
